import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "../../layouts/AdminLayout";
import EngineerLayout from "../../layouts/EngineerLayout";
import API, { UPLOADS_URL } from "../../services/api";
import {
  RefreshCw, Plus, Calendar, ClipboardList, Clock, AlertTriangle,
  CheckCircle2, Search, X, Eye, Pencil, Trash2, Camera, Image as ImageIcon,
  ScrollText, PlayCircle, Square,
} from "lucide-react";
// ── DESIGN TOKENS ─────────────────────────────────────────────────────────────
const DS = {
  indigo:       "#4F46E5",
  indigoMid:    "#6366F1",
  indigoLight:  "#EEF2FF",
  indigoDark:   "#3730A3",
  teal:         "#0D9488",
  tealLight:    "#F0FDFA",
  emerald:      "#059669",
  emeraldLight: "#ECFDF5",
  amber:        "#D97706",
  amberLight:   "#FFFBEB",
  rose:         "#E11D48",
  roseLight:    "#FFF1F2",
  violet:       "#7C3AED",
  violetLight:  "#F5F3FF",
  orange:       "#EA580C",
  orangeLight:  "#FFF7ED",
  bg:           "#F8FAFC",
  surface:      "#FFFFFF",
  surfaceAlt:   "#F8FAFC",
  border:       "#E2E8F0",
  dark:         "#0F172A",
  gray:         "#64748B",
  grayLight:    "#F1F5F9",
  faint:        "#94A3B8",
};

const ROWS_PER_PAGE = 10;

// ── GLOBAL CSS ────────────────────────────────────────────────────────────────
const globalCss = `
 @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .ws-wrap *, .ws-wrap *::before, .ws-wrap *::after { box-sizing: border-box; }
  .ws-wrap {
    font-family: 'Montserrat', sans-serif;
    background: #F8FAFC;
    min-height: 100%;
  }

  /* Table rows */
  .ws-tr { transition: background 0.12s; }
  .ws-tr:hover { background: #F5F3FF !important; }
  .ws-tr:hover .ws-row-num { background: #4F46E5 !important; color: #fff !important; }

  /* Refresh button */
  .ws-btn-refresh {
    display: inline-flex; align-items: center; gap: 7px;
    background: #fff; color: #4F46E5;
    border: 1.5px solid #C7D2FE; border-radius: 10px;
    padding: 8px 16px; font-size: 13px; font-weight: 700;
   cursor: pointer; font-family: 'Montserrat', sans-serif;
    box-shadow: 0 1px 4px rgba(79,70,229,0.08);
    transition: all 0.15s;
  }
  .ws-btn-refresh:hover {
    background: #EEF2FF; border-color: #A5B4FC;
    box-shadow: 0 4px 12px rgba(79,70,229,0.15);
    transform: translateY(-1px);
  }
  .ws-btn-refresh:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
  .ws-refresh-icon { display: inline-block; transition: transform 0.4s; font-size: 15px; }
  .ws-refresh-spinning { animation: wsSpin 0.7s linear infinite; }

  /* Action buttons */
  .ws-btn-action {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 6px 12px; border-radius: 8px;
    font-size: 12px; font-weight: 700;
cursor: pointer; font-family: 'Montserrat', sans-serif;
    border: 1.5px solid transparent;
    transition: all 0.14s;
    white-space: nowrap;
  }

  /* Pagination */
  .ws-pg-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; border-radius: 8px;
    font-size: 13px; font-weight: 700;
   cursor: pointer; font-family: 'Montserrat', sans-serif;
    border: 1.5px solid #E2E8F0;
    background: #fff; color: #64748B;
    transition: all 0.14s;
  }
  .ws-pg-btn:hover:not(:disabled) { border-color: #A5B4FC; color: #4F46E5; background: #EEF2FF; }
  .ws-pg-btn:disabled { opacity: 0.38; cursor: not-allowed; }
  .ws-pg-btn.active {
    background: linear-gradient(135deg,#4F46E5,#6366F1);
    color: #fff; border-color: transparent;
    box-shadow: 0 3px 10px rgba(79,70,229,0.3);
  }

  /* Stat card */
  .ws-stat-card { transition: box-shadow 0.2s, transform 0.2s; }
  .ws-stat-card:hover { box-shadow: 0 12px 32px rgba(0,0,0,0.1) !important; transform: translateY(-2px); }

  /* Scrollbar */
  .ws-table-scroll::-webkit-scrollbar { height: 5px; }
  .ws-table-scroll::-webkit-scrollbar-track { background: transparent; }
  .ws-table-scroll::-webkit-scrollbar-thumb { background: #DDD6FE; border-radius: 99px; }

  /* Searchable dropdown */
  .ws-sdrop-list {
    position: absolute; z-index: 300; top: calc(100% + 4px); left: 0; right: 0;
    background: #fff; border: 1.5px solid #C7D2FE;
    border-radius: 12px; max-height: 200px; overflow-y: auto;
    box-shadow: 0 8px 24px rgba(79,70,229,0.12);
  }
 .ws-sdrop-item {
    padding: 9px 14px; font-size: 13px; font-family: 'Montserrat', sans-serif;
   color: #0F172A; cursor: pointer; transition: background 0.1s;
  }
  .ws-sdrop-item:hover { background: #EEF2FF; color: #4F46E5; }
  .ws-sdrop-item.hint { color: #94A3B8; font-style: italic; font-size: 12px; }

  /* Filter inputs */
  .ws-filter-input {
    width: 100%; border-radius: 10px;
    border: 1.5px solid #E2E8F0;
    padding: 9px 12px; font-size: 13px;
    color: #0F172A; background: #fff;
    outline: none; font-family: 'Montserrat', sans-serif;
    transition: border-color 0.15s;
    height: 38px;
  }
  .ws-filter-input:focus { border-color: #A5B4FC; }
  .ws-filter-select {
    width: 100%; border-radius: 10px;
    border: 1.5px solid #E2E8F0;
    padding: 9px 12px; font-size: 13px;
    color: #0F172A; background: #fff;
   outline: none; font-family: 'Montserrat', sans-serif;
    transition: border-color 0.15s;
    height: 38px; cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%2394A3B8' d='M1 1l5 5 5-5'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    padding-right: 28px;
  }
  .ws-filter-select:focus { border-color: #A5B4FC; }

  @keyframes wsSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  /* Remove photo (X) button */
  .ws-photo-remove-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 22px; height: 22px; border-radius: 50%;
    background: #FFF1F2; color: #E11D48;
    border: 1.5px solid #FECDD3;
    font-size: 12px; font-weight: 800;
    cursor: pointer; font-family: 'Montserrat', sans-serif;
    flex-shrink: 0; line-height: 1;
    transition: all 0.14s;
  }
  .ws-photo-remove-btn:hover { background: #E11D48; color: #fff; }

  @media (max-width: 768px) {
    .ws-stats-grid { grid-template-columns: 1fr 1fr !important; }
    .ws-page-header { flex-direction: column !important; align-items: flex-start !important; }
    .ws-filters-grid { grid-template-columns: 1fr 1fr !important; }
  }
  @media (max-width: 480px) {
    .ws-stats-grid { grid-template-columns: 1fr !important; }
    .ws-filters-grid { grid-template-columns: 1fr !important; }
  }
`;

// ── SEARCHABLE EDITABLE DROPDOWN COMPONENT ───────────────────────────────────
// CHANGES 12, 13, 14: Phase and Task fields become searchable + free-text
function SearchableDropdown({ value, onChange, options, placeholder, label, focusColor = DS.indigo }) {
  const [query,    setQuery]    = useState(value || "");
  const [open,     setOpen]     = useState(false);
  const [focused,  setFocused]  = useState(false);
  const wrapRef = useRef(null);

  // Sync external value changes (e.g. edit mode)
  useEffect(() => { setQuery(value || ""); }, [value]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = options.filter(o =>
    (o.label || "").toLowerCase().includes((query || "").toLowerCase())
  );

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);
    // Free-text: pass the typed string directly as value
    onChange(val);
  };

  const handleSelect = (opt) => {
    setQuery(opt.label);
    onChange(opt.value, opt.label);
    setOpen(false);
  };

  const showHint = open && query && !options.find(o => o.label.toLowerCase() === query.toLowerCase());

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      {label && (
        <label style={{ fontSize: 12, fontWeight: 600, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.06em",fontFamily: "'Montserrat', sans-serif" }}>
          {label}
        </label>
      )}
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <input
          type="text"
          value={query}
          placeholder={placeholder || "Search or type…"}
          onChange={handleInput}
          onFocus={() => { setFocused(true); setOpen(true); }}
          onBlur={() => setFocused(false)}
          style={{
            width: "100%", borderRadius: 12,
            border: `1.5px solid ${focused || open ? focusColor : DS.border}`,
            padding: "11px 36px 11px 14px", fontSize: 14,
            color: DS.dark, background: DS.surfaceAlt,
            outline: "none", fontFamily: "'Montserrat', sans-serif",
            boxSizing: "border-box", height: "43px",
            transition: "border-color 0.15s",
          }}
        />
        <span
          style={{ position: "absolute", right: 12, fontSize: 11, color: DS.faint, cursor: "pointer", userSelect: "none" }}
          onMouseDown={e => { e.preventDefault(); setOpen(o => !o); }}
        >▾</span>
      </div>

      {open && (
        <div className="ws-sdrop-list">
          {filtered.length === 0 && !query && (
            <div className="ws-sdrop-item hint">Start typing to search…</div>
          )}
          {filtered.length === 0 && query && (
            <div className="ws-sdrop-item hint">Press Enter or keep typing to use "{query}"</div>
          )}
          {filtered.map(opt => (
            <div key={opt.value} className="ws-sdrop-item" onMouseDown={() => handleSelect(opt)}>
              {opt.label}
            </div>
          ))}
          {showHint && (
            <div
              className="ws-sdrop-item"
              style={{ borderTop: `1px dashed ${DS.border}`, color: DS.indigo, fontWeight: 700 }}
              onMouseDown={() => { onChange(query); setOpen(false); }}
            >
              ＋ Use "{query}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function WorkSchedule({ layout = "admin" }) {
  const { id: urlProjectId } = useParams();
  const navigate = useNavigate();

  // ── Core data state ────────────────────────────────────────────────────────
  const [projects,       setProjects]       = useState([]);
  const [phases,         setPhases]         = useState([]);
  const [tasks,          setTasks]          = useState([]);
  const [workItems,      setWorkItems]      = useState([]);
  const [editId,         setEditId]         = useState(null);
  const [viewItem,       setViewItem]       = useState(null);
  const [currentProject, setCurrentProject] = useState(null);

  // ── Logs state (UNCHANGED) ─────────────────────────────────────────────────
  const [logsMap,        setLogsMap]        = useState({});
  const [expandedLogs,   setExpandedLogs]   = useState(new Set());
  const [logLoading,     setLogLoading]     = useState(new Set());
  const [progressModal,  setProgressModal]  = useState({ open: false, wid: null });
  const [progressForm,   setProgressForm]   = useState({ comment: "", image: null });
  const [progressSaving, setProgressSaving] = useState(false);
  const progressImageRef = useRef(null);
  const [existingPhotos, setExistingPhotos] = useState({ before_photo: null, after_photo: null });
  // NEW: tracks whether the user clicked the ✕ to remove an existing photo
  const [removePhotoFlags, setRemovePhotoFlags] = useState({ before_photo: false, after_photo: false });
  const resolvedProjectIdRef = useRef(null);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);

  // ── MILESTONE — 50% email button state ────────────────────────────────────
const [milestoneStatus,  setMilestoneStatus]  = useState({ percent: 0, sent: false, reachedHalf: false, completedSqmt: 0, totalAreaSqmt: 0 });
  const [milestoneSending, setMilestoneSending]  = useState(false);
  const [showForm,    setShowForm]    = useState(false);
  const [search,      setSearch]      = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState(null); // { message, type: "success" | "error" }

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // CHANGE 8: Project filter (admin)
  const [filterProject, setFilterProject] = useState("");
  // CHANGE 9: Status filter
  const [filterStatus,  setFilterStatus]  = useState("");
  // CHANGE 10: Date range filter
  const [filterFrom,    setFilterFrom]    = useState("");
  const [filterTo,      setFilterTo]      = useState("");
  // CHANGE 11: Due task toggle
  const [filterDue,     setFilterDue]     = useState(false);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    project_id:     "",
    phase_id:       "",
    phase_name:     "",   // for searchable dropdown
    task_id:        "",
    task_name:      "",   // for searchable dropdown
    sqmt:           "",
    completed_sqmt: "",
    start_date:     "",
    end_date:       "",
    status:         "pending",
    before_photo:   null,
    after_photo:    null,
  });

  // NEW: multiple task rows for a single phase (Task | Target Sqmt | Completed Sqmt)
  const [taskRows, setTaskRows] = useState([
    { id: 1, task_id: "", task_name: "", sqmt: "", completed_sqmt: "" },
  ]);

  const storedRole = (localStorage.getItem("role") || "").toLowerCase();
  const storedType = (localStorage.getItem("type") || "").toLowerCase();
  const storedIsAdmin = Number(localStorage.getItem("is_admin"));
  // alert(storedIsAdmin)
  // alert(localStorage.getItem("is_admin"))
  const isEngineer =  (storedType === "engineer") || false;
  const Layout     = storedIsAdmin === 1 || storedType === "admin" ?  AdminLayout : EngineerLayout;

 const fetchWorkItems = useCallback(async (overrideProjectId, isRefresh = false) => {
  try {
    if (!isRefresh) setLoading(true);
    let res;
    const cacheBust = {
      headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
      params: { _t: Date.now() },
    };
    if (isEngineer && urlProjectId) {
      // alert()
      const pid = overrideProjectId || resolvedProjectIdRef.current;
      if (!pid) {
        console.warn("[WorkSchedule] fetchWorkItems: project id not resolved yet, skipping fetch");
        return;
      }
      res = await API.get(`/engineers/me/projects/${pid}/schedule`, cacheBust);
    } else {//alert("admin")
      if(storedIsAdmin ==1){  
         res = await API.get("/admin/schedules", cacheBust);
      }else{
        res = await API.get(`/admin/schedules/manager/${localStorage.getItem("id")}`, cacheBust);
      }
    }
const items = res.data?.schedules || res.data?.data || [];
    console.log("[DEBUG] workItems sample:", items[0]);
    const sortedItems = [...items].sort((a, b) => (a.id || 0) - (b.id || 0));
    setWorkItems([]);
    setTimeout(() => {
      setWorkItems(sortedItems);
      setCurrentPage(1);
    }, 50);
  } catch (err) {
    console.error("[WorkSchedule] fetchWorkItems error:", err);
  } finally {
    if (!isRefresh) setLoading(false);
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [isEngineer, urlProjectId]);

  

  

  // ── MILESTONE — fetch current % + whether email already sent ─────────────
  const fetchMilestoneStatus = useCallback(async (pid) => {
    if (!pid) return;
    try {
      const res = await API.get(`/engineers/me/projects/${pid}/milestone-status`);
if (res.data?.success) {
        setMilestoneStatus({
          percent: res.data.percent,
          sent: res.data.milestone_50_sent,
          reachedHalf: res.data.reachedHalf,
          completedSqmt: res.data.completedSqmt,
          totalAreaSqmt: res.data.totalAreaSqmt,
        });
      }
    } catch (err) {
      console.error("[WorkSchedule] fetchMilestoneStatus error:", err);
    }
  }, []);

  // ── MILESTONE — send the 50% email, then hide the button permanently ─────
  useEffect(() => {
    if (!isEngineer) {
      if (filterProject) {
        fetchMilestoneStatus(filterProject);
      } else {
        setMilestoneStatus({ percent: 0, sent: false, reachedHalf: false, completedSqmt: 0, totalAreaSqmt: 0 });
      }
    }
  }, [filterProject, isEngineer, fetchMilestoneStatus]);

  const handleSendMilestone = async () => {
    const pid = resolvedProjectIdRef.current || (currentProject && currentProject.id) || filterProject;
    if (!pid) return;
    setMilestoneSending(true);
    try {
      const res = await API.post(`/engineers/me/projects/${pid}/send-milestone`);
      if (res.data?.success) {
        setToast({ message: "Milestone email sent to client!", type: "success" });
        setMilestoneStatus(prev => ({ ...prev, sent: true }));
      } else {
        setToast({ message: res.data?.message || "Failed to send milestone email.", type: "error" });
      }
    } catch (err) {
      setToast({ message: err.response?.data?.message || "Error sending milestone email.", type: "error" });
    } finally {
      setMilestoneSending(false);
    }
  };

  const fetchPhasesAndTasks = useCallback(async (projectId) => {
  if (!projectId) { setPhases([]); setTasks([]); return; }
  try {
    const [phaseRes, taskRes] = await Promise.all([
      API.get(`/admin/phases?project_id=${projectId}`).catch(() => ({ data: { data: [] } })),
      API.get(`/admin/tasks?project_id=${projectId}`).catch(()  => ({ data: { data: [] } })),
    ]);
    setPhases(phaseRes.data.data || phaseRes.data.phases || []);
    setTasks( taskRes.data.data  || taskRes.data.tasks   || []);
  } catch (err) {
    console.error("[WorkSchedule] fetchPhasesAndTasks error:", err);
  }
}, []);

  // ── Logs handlers (UNCHANGED) ──────────────────────────────────────────────
  const fetchLogs = async (wid) => {
    setLogLoading(prev => new Set(prev).add(wid));
    try {
      const res = await API.get(`/work-schedule-logs/${wid}`);
      setLogsMap(prev => ({ ...prev, [wid]: res.data.logs || [] }));
    } catch (err) {
      console.error(`[WorkSchedule] fetchLogs error wid=${wid}:`, err);
      setLogsMap(prev => ({ ...prev, [wid]: [] }));
    } finally {
      setLogLoading(prev => { const n = new Set(prev); n.delete(wid); return n; });
    }
  };

  const toggleLogs = (wid) => {
    setExpandedLogs(prev => {
      const next = new Set(prev);
      if (next.has(wid)) { next.delete(wid); }
      else { next.add(wid); fetchLogs(wid); }
      return next;
    });
  };

  const openProgressModal  = (wid) => {
    setProgressForm({ comment: "", image: null });
    if (progressImageRef.current) progressImageRef.current.value = "";
    setProgressModal({ open: true, wid });
  };
  const closeProgressModal = () => {
    setProgressModal({ open: false, wid: null });
    setProgressForm({ comment: "", image: null });
    if (progressImageRef.current) progressImageRef.current.value = "";
  };

const handleAddLog = async () => {
    if (!progressForm.comment.trim() && !progressForm.image) {
      setToast({ message: "Please add a comment or image before saving.", type: "error" });
      return;
    }
    setProgressSaving(true);
    try {
      const added_by = localStorage.getItem("userId") || localStorage.getItem("user_id") || localStorage.getItem("id") || "";
      const form = new FormData();
      form.append("wid",     progressModal.wid);
      form.append("comment", progressForm.comment.trim());
      if (progressForm.image) form.append("progress_image", progressForm.image);
      if (added_by) form.append("added_by", added_by);

      const res = await API.post("/work-schedule-logs/add", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
     if (res.data.success) {
        setToast({ message: "Progress log saved successfully!", type: "success" });
        await fetchLogs(progressModal.wid);
        setExpandedLogs(prev => new Set(prev).add(progressModal.wid));
        closeProgressModal();
      } else {
        setToast({ message: res.data.message || "Failed to save log.", type: "error" });
      }
    } catch (err) {
      console.error("[WorkSchedule] handleAddLog error:", err);
      setToast({ message: err.response?.data?.message || "Error saving progress log.", type: "error" });
    } finally {
      setProgressSaving(false);
    }
  };

  const handleDeleteLog = async (logId, wid) => {
    if (!window.confirm("Delete this progress log?")) return;
    try {
      const res = await API.delete(`/work-schedule-logs/${logId}`);
      if (res.data.success) {
        setToast({ message: "Log deleted successfully!", type: "delete" });
        await fetchLogs(wid);
      } else {
        setToast({ message: res.data.message || "Failed to delete log.", type: "error" });
      }
    } catch (err) {
      console.error("[WorkSchedule] handleDeleteLog error:", err);
      setToast({ message: err.response?.data?.message || "Error deleting log.", type: "error" });
    }
  };



  // ── Static data (phases, tasks, projects) ─────────────────────────────────
//  useEffect(() => {
  //  Promise.all([
    //  API.get("/admin/phases").catch(() => ({ data: { data: [] } })),
     // API.get("/admin/tasks").catch(()  => ({ data: { data: [] } })),
    //]).then(([phaseRes, taskRes]) => {
     // setPhases(phaseRes.data.data  || phaseRes.data.phases  || []);
      //setTasks( taskRes.data.data   || taskRes.data.tasks    || []);
    //});
// ── Static data (projects) ────────────────────────────────────────────────
useEffect(() => {
  console.log("[DEBUG] effect fired — isEngineer:", isEngineer, "storedType:", storedType, "storedIsAdmin:", storedIsAdmin, "raw is_admin:", localStorage.getItem("is_admin"), "raw type:", localStorage.getItem("type"));
  if (!isEngineer) {
    console.log("[DEBUG] calling fetchWorkItems() now");
    const isAdmin = storedIsAdmin === 1 || storedRole === "admin";
    const managerId = localStorage.getItem("id");
    const projEndpoint = isAdmin ? "/projects" : (managerId ? `/projects/manager/${managerId}` : null);
    if (projEndpoint) {
      API.get(projEndpoint)
        .then(res => {
          const projs = res.data.projects || [];
          setProjects(projs);
        })
        .catch(err => console.error("[WorkSchedule] projects fetch error:", err));
    }
    fetchWorkItems();
  } else {
    console.log("[DEBUG] SKIPPED fetchWorkItems — isEngineer is truthy");
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [isEngineer]);

  // ── Resolve current project (engineer only) — UNCHANGED ───────────────────
  useEffect(() => {
    if (!urlProjectId || !isEngineer) return;

    const toPositiveInt = (v) => {
      if (v === null || v === undefined || v === "") return null;
      const n = Number(v);
      return (Number.isFinite(n) && Number.isInteger(n) && n > 0) ? n : null;
    };

    (async () => {
      try {
        const assignRes   = await API.get("/engineers/me/projects");
        const assignments = assignRes.data?.projects || [];
        const matched     = assignments.find(
          (a) =>
            String(a.id) === String(urlProjectId) ||
            String(a.real_project_id) === String(urlProjectId) ||
            String(a.project_id) === String(urlProjectId)
        );
        if (!matched) { console.error("[WorkSchedule] no assignment for id:", urlProjectId); return; }

        const resolvedId = toPositiveInt(matched.real_project_id);
       if (resolvedId) {
          resolvedProjectIdRef.current = resolvedId;
          const projRes = await API.get(`/projects/${resolvedId}`);
          if (projRes.data?.success) {
            const proj = projRes.data.project;
            setCurrentProject(proj);
            setFormData(prev => ({ ...prev, project_id: proj.id }));
            fetchWorkItems(proj.id);
            fetchPhasesAndTasks(proj.id);
            fetchMilestoneStatus(resolvedId);
          }
        } else {
          const uid = matched.project_uid || null;
          if (!uid) return;
          const projRes = await API.get(`/projects/${uid}`);
          if (projRes.data?.success && projRes.data.project?.id) {
            const proj = projRes.data.project;
            resolvedProjectIdRef.current = proj.id;
            setCurrentProject(proj);
            setFormData(prev => ({ ...prev, project_id: proj.id }));
            fetchWorkItems(proj.id);
            fetchMilestoneStatus(proj.id);
          }
        }
      } catch (err) {
        console.error("[WorkSchedule] project resolution error:", err);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlProjectId, isEngineer]);

  // ── Form handlers ──────────────────────────────────────────────────────────
  const handleChange = (e) => {
  const { name, value, files } = e.target;
  setFormData(prev => ({ ...prev, [name]: files ? files[0] : value }));
  // If the user picks a NEW file after having clicked ✕, un-flag the removal
  // (their new upload takes precedence — this is a safe, sensible default)
  if ((name === "before_photo" || name === "after_photo") && files && files[0]) {
    setRemovePhotoFlags(prev => ({ ...prev, [name]: false }));
  }
  if (name === "project_id") {
    // Reset phase and task when project changes
    setFormData(prev => ({ ...prev, project_id: value, phase_id: "", phase_name: "", task_id: "", task_name: "" }));
    fetchPhasesAndTasks(value);
  }
};

  // NEW: handle clicking the ✕ next to an existing photo preview
  const handleRemoveExistingPhoto = (field) => {
    setRemovePhotoFlags(prev => ({ ...prev, [field]: true }));
  };

  // CHANGES 12, 13: Phase/Task searchable dropdown handlers
  const handlePhaseChange = (val, label) => {
    // If an option was selected (has both val and label, val is numeric id)
    const matchedPhase = phases.find(p => String(p.id) === String(val));
    if (matchedPhase) {
      setFormData(prev => ({ ...prev, phase_id: matchedPhase.id, phase_name: matchedPhase.name }));
    } else {
      // Free-text entry: clear phase_id, store raw text as phase_name
      setFormData(prev => ({ ...prev, phase_id: "", phase_name: val }));
    }
  };

  const handleTaskChange = (val, label) => {
    const matchedTask = tasks.find(t => String(t.id) === String(val));
    if (matchedTask) {
      setFormData(prev => ({ ...prev, task_id: matchedTask.id, task_name: matchedTask.title }));
    } else {
      setFormData(prev => ({ ...prev, task_id: "", task_name: val }));
    }
  };

  // NEW: task-row handlers for multi-task-per-phase
  const addTaskRow = () => {
    setTaskRows(prev => [
      ...prev,
      { id: Date.now(), task_id: "", task_name: "", sqmt: "", completed_sqmt: "" },
    ]);
  };

  const removeTaskRow = (id) => {
    setTaskRows(prev => (prev.length > 1 ? prev.filter(r => r.id !== id) : prev));
  };

  const updateTaskRow = (id, field, value) => {
    setTaskRows(prev => prev.map(r => (r.id === id ? { ...r, [field]: value } : r)));
  };

  const handleTaskRowChange = (id, val) => {
    const matchedTask = tasks.find(t => String(t.id) === String(val));
    setTaskRows(prev =>
      prev.map(r => {
        if (r.id !== id) return r;
        return matchedTask
          ? { ...r, task_id: matchedTask.id, task_name: matchedTask.title }
          : { ...r, task_id: "", task_name: val };
      })
    );
  };

 const handleSave = async () => {
    if (!formData.project_id && !resolvedProjectIdRef.current) {
      setToast({ message: "Please select a Project.", type: "error" });
      return;
    }
    if (!formData.phase_id && !formData.phase_name) {
      setToast({ message: "Please fill in the Phase field.", type: "error" });
      return;
    }
    const invalidRow = taskRows.find(r => !r.task_id && !r.task_name.trim());
    if (invalidRow) {
      setToast({ message: "Please fill in the Task field for every row.", type: "error" });
      return;
    }

    const pid = formData.project_id || resolvedProjectIdRef.current;

    try {
      if (editId) {
        // EDIT MODE — single task row (unchanged behavior)
        const row = taskRows[0];
        const form = new FormData();
        const payload = {
          ...formData,
          task_id: row.task_id, task_name: row.task_name,
          sqmt: row.sqmt, completed_sqmt: row.completed_sqmt,
        };
        if (!payload.phase_id && payload.phase_name) payload.phase_id = payload.phase_name;
        if (!payload.task_id && payload.task_name) payload.task_id = payload.task_name;
        Object.keys(payload).forEach(key => {
          if (payload[key] !== null && payload[key] !== undefined && payload[key] !== "") form.append(key, payload[key]);
        });
        if (removePhotoFlags.before_photo) form.append("remove_before_photo", "1");
        if (removePhotoFlags.after_photo)  form.append("remove_after_photo", "1");

        const res = await API.put(`/engineers/me/schedule/${editId}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.data.success) {
          setToast({ message: "Work Schedule updated successfully!", type: "success" });
          handleReset();
          fetchWorkItems();
          if (isEngineer) fetchMilestoneStatus(resolvedProjectIdRef.current);
        } else {
          setToast({ message: res.data.message || "Save failed.", type: "error" });
        }
      } else {
        // CREATE MODE — one request per task row, same phase/dates/status/photos
        let successCount = 0;
        for (const row of taskRows) {
          const form = new FormData();
          const payload = {
            project_id:     formData.project_id,
            phase_id:       formData.phase_id,
            phase_name:     formData.phase_name,
            task_id:        row.task_id,
            task_name:      row.task_name,
            sqmt:           row.sqmt,
            completed_sqmt: row.completed_sqmt,
            start_date:     formData.start_date,
            end_date:       formData.end_date,
            status:         formData.status,
            before_photo:   formData.before_photo,
            after_photo:    formData.after_photo,
          };
          if (!payload.phase_id && payload.phase_name) payload.phase_id = payload.phase_name;
          if (!payload.task_id && payload.task_name)  payload.task_id  = payload.task_name;
          Object.keys(payload).forEach(key => {
            if (payload[key] !== null && payload[key] !== undefined && payload[key] !== "") form.append(key, payload[key]);
          });

          const res = await API.post(`/engineers/me/projects/${pid}/schedule`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (res.data.success) successCount++;
        }

        if (successCount === taskRows.length) {
          setToast({ message: `${successCount} schedule item(s) saved successfully!`, type: "success" });
        } else {
          setToast({ message: `${successCount}/${taskRows.length} saved — some rows failed.`, type: "error" });
        }
        handleReset();
        fetchWorkItems();
        if (isEngineer) fetchMilestoneStatus(resolvedProjectIdRef.current);
      }
    } catch (err) {
      console.error("[WorkSchedule] handleSave error:", err);
      setToast({ message: err.response?.data?.message || "Error saving schedule", type: "error" });
    }
  };

  const handleEdit = (item) => {
  setEditId(item.id);
  setFormData({
    project_id:     item.project_id     || "",
    phase_id:       item.phase_id       || "",
    phase_name:     item.phase_name      || "",
    task_id:        item.task_id        || "",
    task_name:      item.task_name         || "",
    sqmt:           item.sqmt           || "",
    completed_sqmt: item.completed_sqmt || "",
    start_date:     item.start_date?.split("T")[0] || "",
    end_date:       item.end_date?.split("T")[0]   || "",
    status:         item.status         || "pending",
    before_photo:   null, // file input stays empty — browser can't pre-fill files
    after_photo:    null,
  });
  // Store existing photo filenames separately so the form can preview them
  setExistingPhotos({
    before_photo: item.before_photo || null,
    after_photo:  item.after_photo  || null,
  });
// Reset any pending removal flags from a previous edit session
  setRemovePhotoFlags({ before_photo: false, after_photo: false });
  setTaskRows([{
    id: 1,
    task_id: item.task_id || "",
    task_name: item.task_name || "",
    sqmt: item.sqmt || "",
    completed_sqmt: item.completed_sqmt || "",
  }]);
  fetchPhasesAndTasks(item.project_id);
  setShowForm(true);
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this schedule?")) return;
    try {
      const res = await API.delete(`/engineers/me/schedule/${id}`);
      if (res.data.success) {
        setToast({ message: "Deleted successfully!", type: "delete" });
        fetchWorkItems();
      }
    } catch (err) {
      setToast({ message: err.response?.data?.message || "Error deleting schedule", type: "delete" });
    }
  };
  const handleReset = () => {
  setEditId(null);
  setFormData({
    project_id:     resolvedProjectIdRef.current || (currentProject?.id ?? ""),
    phase_id:       "",
    phase_name:     "",
    task_id:        "",
    task_name:      "",
    sqmt:           "",
    completed_sqmt: "",
    start_date:     "",
    end_date:       "",
    status:         "pending",
    before_photo:   null,
    after_photo:    null,
  });
setExistingPhotos({ before_photo: null, after_photo: null });
  setRemovePhotoFlags({ before_photo: false, after_photo: false });
  setTaskRows([{ id: 1, task_id: "", task_name: "", sqmt: "", completed_sqmt: "" }]);
  setShowForm(false);
};
  // ── Display helpers ────────────────────────────────────────────────────────
  const getProjectName = (id) => String(projects.find(p => String(p.id) === String(id))?.title || id || "");
  const getPhaseName   = (id) => String(phases.find(p => String(p.id)   === String(id))?.name  || id || "");
  const getTaskName    = (id) => String(tasks.find(t => String(t.id)    === String(id))?.title || id || "");

  const formatDate = (dt) => {
    if (!dt) return "—";
    try { return new Date(dt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return dt; }
  };

  const getStatusStyle = (status) => {
    const s = (status || "pending").toLowerCase();
    if (s === "completed")                          return { color: DS.emerald, bg: DS.emeraldLight, border: "#A7F3D0" };
    if (s === "in_progress" || s === "in progress") return { color: DS.indigo,  bg: DS.indigoLight,  border: "#C7D2FE" };
    if (s === "delayed")                            return { color: DS.rose,    bg: DS.roseLight,    border: "#FECDD3" };
    return                                                 { color: DS.amber,   bg: DS.amberLight,   border: "#FDE68A" };
  };

  // ── Derived: all unique statuses from live data (CHANGE 9) ────────────────
  const uniqueStatuses = Array.from(
    new Set(workItems.map(i => (i.status || "pending").toLowerCase()))
  ).sort();

  // ── KPI counts (CHANGE 1) ──────────────────────────────────────────────────
  const totalCount     = workItems.length;
  const pendingCount   = workItems.filter(i => (i.status || "").toLowerCase() === "pending").length;
  const inProgressCount= workItems.filter(i => ["in_progress","in progress"].includes((i.status||"").toLowerCase())).length;
  const completedCount = workItems.filter(i => (i.status || "").toLowerCase() === "completed").length;
  const delayedCount   = workItems.filter(i => (i.status || "").toLowerCase() === "delayed").length;

  // CHANGE 11: overdue = end_date < today AND not completed
  const today = new Date(); today.setHours(0,0,0,0);
  const dueCount = workItems.filter(i => {
    if (!i.end_date) return false;
    if ((i.status || "").toLowerCase() === "completed") return false;
    return new Date(i.end_date) < today;
  }).length;

  // ── Filtered items (CHANGES 8-11 + search) ────────────────────────────────
  const filteredItems = workItems.filter(item => {
    // Search
    const searchLower = search.toLowerCase();
    const matchSearch = !search || (
      (item.project_title || getProjectName(item.project_id)).toLowerCase().includes(searchLower) ||
      (item.phase_name    || getPhaseName(item.phase_id)).toLowerCase().includes(searchLower)     ||
      (item.task_name     || getTaskName(item.task_id)).toLowerCase().includes(searchLower)       ||
      (item.status        || "").toLowerCase().includes(searchLower)
    );

    // CHANGE 8: project filter (admin only)
    const matchProject = !filterProject || String(item.project_id) === String(filterProject);

    // CHANGE 9: status filter
    const matchStatus = !filterStatus || (item.status || "pending").toLowerCase() === filterStatus.toLowerCase();

    // CHANGE 10: date range filter (uses start_date)
    const itemDate = item.start_date ? new Date(item.start_date) : null;
    const matchFrom = !filterFrom || (itemDate && itemDate >= new Date(filterFrom));
    const matchTo   = !filterTo   || (itemDate && itemDate <= new Date(filterTo));

    // CHANGE 11: due task filter
    const matchDue = !filterDue || (() => {
      if (!item.end_date) return false;
      if ((item.status || "").toLowerCase() === "completed") return false;
      return new Date(item.end_date) < today;
    })();

    return matchSearch && matchProject && matchStatus && matchFrom && matchTo && matchDue;
  });

  // CHANGE 6: Pagination
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / ROWS_PER_PAGE));
  const safePage   = Math.min(currentPage, totalPages);
  const pageStart  = (safePage - 1) * ROWS_PER_PAGE;
  const pageRows   = filteredItems.slice(pageStart, pageStart + ROWS_PER_PAGE);

  const handleFilterChange = (setter) => (val) => { setter(val); setCurrentPage(1); };

  const getPageNumbers = () => {
    const pages = [];
    let start = Math.max(1, safePage - 2);
    let end   = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  };

  // ── Dropdown options for SearchableDropdown ────────────────────────────────
  const phaseOptions = phases.map(p => ({ value: String(p.name), label: p.name }));
  const taskOptions  = tasks.map(t  => ({ value: String(t.title), label: t.title }));

  // KPI card definitions (CHANGE 1)
  const statCards = [
  { label: "Total Items",    value: totalCount,      accent: DS.indigoMid, color: DS.indigoMid, icon: <ClipboardList size={20} /> },
  { label: "Pending",        value: pendingCount,    accent: DS.amber,     color: DS.amber,     icon: <Clock size={20} /> },
  { label: "In Progress",    value: inProgressCount, accent: DS.indigo,    color: DS.indigo,    icon: <RefreshCw size={20} /> },
  { label: "Completed",      value: completedCount,  accent: DS.emerald,   color: DS.emerald,   icon: <CheckCircle2 size={20} /> },
  { label: "Delayed / Due",  value: dueCount,        accent: DS.rose,      color: DS.rose,      icon: <AlertTriangle size={20} /> },
];

  const hasActiveFilters = filterProject || filterStatus || filterFrom || filterTo || filterDue || search;

  const clearFilters = () => {
    setFilterProject(""); setFilterStatus(""); setFilterFrom("");
    setFilterTo(""); setFilterDue(false); setSearch(""); setCurrentPage(1);
  };

  // ─────────────────────────────────────────────────────────────────────────
const TOAST_STYLES = {
    success: { bg: DS.emerald, icon: <CheckCircle2 size={16} /> },
    error:   { bg: DS.amber,   icon: <AlertTriangle size={16} /> },
    delete:  { bg: DS.rose,    icon: <Trash2 size={16} /> },
  };

  return (
    <Layout>
      <style>{globalCss}</style>
      <style>{`@keyframes ws-toast-in { from { opacity:0; transform:translateY(12px) scale(0.97); } to { opacity:1; transform:none; } }`}</style>

      {toast && (
        <div style={{
          position: "fixed", top: 24, right: 24, zIndex: 99999,
          background: TOAST_STYLES[toast.type]?.bg || TOAST_STYLES.success.bg,
          color: "#fff", padding: "14px 18px", borderRadius: 14,
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          display: "flex", alignItems: "center", gap: 12,
          minWidth: 280, maxWidth: 380,
          animation: "ws-toast-in 0.22s ease",
          fontFamily: "'Montserrat', sans-serif",
        }}>
          <span style={{ display: "inline-flex", flexShrink: 0 }}>
            {TOAST_STYLES[toast.type]?.icon || <CheckCircle2 size={16} />}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{toast.message}</div>
          </div>
          <button onClick={() => setToast(null)} style={{
            background: "rgba(255,255,255,0.2)", border: "none", color: "#fff",
            width: 24, height: 24, borderRadius: "50%", cursor: "pointer",
            fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}><X size={12} /></button>
        </div>
      )}

      <div className="ws-wrap" style={{ flex: 1, padding: "28px 28px 40px", overflowY: "auto" }}>
{/* ── BACK BUTTON ──────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={() =>
              isEngineer && urlProjectId
                ? navigate(`/engineer/project/${urlProjectId}`)
                : navigate("/admin-dashboard")
            }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#fff",
                border: "1.5px solid #E2E8F0",
                borderRadius: 8,
                padding: "6px 14px",
                fontSize: 12,
                fontWeight: 600,
                color: DS.indigo,
                cursor: "pointer",
                fontFamily: "'Montserrat', sans-serif",
                transition: "all 0.18s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = DS.indigo;
                e.currentTarget.style.background = DS.indigoLight;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#E2E8F0";
                e.currentTarget.style.background = "#fff";
              }}
            >
{isEngineer && urlProjectId ? "← Back to Project" : "← Back to Dashboard"}
          </button>
        </div>

        {/* ── PAGE HEADER ─────────────────────────────────────────────────── */}
        <div className="ws-page-header" style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 26, flexWrap: "wrap", gap: 16,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
  width: 52, height: 52, borderRadius: 15,
  background: "linear-gradient(135deg,#4F46E5,#6366F1)",
  display: "flex", alignItems: "center", justifyContent: "center",
  boxShadow: "0 6px 18px rgba(79,70,229,0.3)",
}}>
  <Calendar size={22} color="#fff" />
</div>
            <div>
              <h1 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 24, color: DS.dark, margin: 0, letterSpacing: "-0.4px" }}>
                Work Schedule
              </h1>
              {isEngineer && currentProject && (
                <div style={{ fontSize: 13, color: DS.gray, marginTop: 3, fontFamily: "'Montserrat', sans-serif" }}>
                  {currentProject.title} · {currentProject.project_uid}
                </div>
              )}
              {!isEngineer && (
                <div style={{ fontSize: 13, color: DS.gray, marginTop: 3, fontFamily: "'Montserrat', sans-serif" }}>
                  Manage and track all project work schedules
                </div>
              )}
            </div>
          </div>

         <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {/* MILESTONE — only shows once ≥50% and only until sent once */}
           {(isEngineer ? (currentProject && milestoneStatus.reachedHalf && !milestoneStatus.sent) : (filterProject && milestoneStatus.reachedHalf && !milestoneStatus.sent)) && (
              <button
                onClick={handleSendMilestone}
                disabled={milestoneSending}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 7,
                  background: milestoneSending ? DS.grayLight : "linear-gradient(135deg,#059669,#0D9488)",
                  color: milestoneSending ? DS.faint : "#fff",
                  border: "none", borderRadius: 10, padding: "8px 16px",
                  fontSize: 13, fontWeight: 700, cursor: milestoneSending ? "not-allowed" : "pointer",
                  fontFamily: "'Montserrat', sans-serif",
                  boxShadow: milestoneSending ? "none" : "0 4px 14px rgba(5,150,105,0.3)",
                }}
              >
               <CheckCircle2 size={16} />
                {milestoneSending ? "Sending…" : "Notify Client — 50% Milestone Reached"}
              </button>
            )}

{/* CHANGE 2: Refresh button — ProjectList style */}
<button
  className="ws-btn-refresh"
  onClick={async () => {
    setRefreshing(true);
    const minSpin = new Promise(resolve => setTimeout(resolve, 700));
    await Promise.all([fetchWorkItems(resolvedProjectIdRef.current, true), minSpin]);
    setRefreshing(false);
  }}
  disabled={refreshing}
>
  <RefreshCw size={16} className={refreshing ? "ws-refresh-spinning" : ""} />
</button>

            <button
              onClick={() => { if (showForm) handleReset(); else setShowForm(true); }}
              style={{
                background: "linear-gradient(135deg,#4F46E5,#6366F1)",
                color: "#fff", border: "none", padding: "9px 20px",
                borderRadius: 10, fontSize: 13, fontWeight: 700,
                cursor: "pointer", fontFamily: "'Montserrat', sans-serif",
                boxShadow: "0 4px 14px rgba(79,70,229,0.3)",
                display: "flex", alignItems: "center", gap: 7,
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = "0 6px 20px rgba(79,70,229,0.45)"}
              onMouseLeave={e => e.currentTarget.style.boxShadow = "0 4px 14px rgba(79,70,229,0.3)"}
            >
              <Plus size={16} />
{editId ? "Edit Schedule" : showForm ? "Close Form" : "Add Schedule"}
            </button>
          </div>
        </div>

      

        {/* ── COLLAPSIBLE FORM ─────────────────────────────────────────────── */}
        {showForm && (
          <div style={{
            background: DS.surface, borderRadius: 18,
            border: `1.5px solid ${DS.border}`, overflow: "hidden",
            marginBottom: 24, boxShadow: "0 4px 20px rgba(79,70,229,0.1)",
          }}>
            <div style={{ height: 4, background: "linear-gradient(90deg,#4F46E5,#E11D48,#D97706)" }} />
            <div style={{ padding: "24px 26px" }}>
<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 22, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: DS.indigoLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Calendar size={17} color={DS.indigo} />
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 16, color: DS.dark }}>
                      {editId ? "Edit Work Schedule" : "Create New Work Schedule"}
                    </div>
                    <div style={{ fontSize: 12, color: DS.gray, fontFamily: "'Montserrat', sans-serif" }}>Fill in the details below</div>
                  </div>
                </div>

                {/* NEW: Back button — closes form and navigates to previous page */}
                <button
                  onClick={() => {
                    handleReset();
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    background: "#fff", border: "1.5px solid #E2E8F0",
                    borderRadius: 8, padding: "6px 14px", fontSize: 12,
                    fontWeight: 600, color: DS.indigo, cursor: "pointer",
                    fontFamily: "'Montserrat', sans-serif", transition: "all 0.18s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = DS.indigo; e.currentTarget.style.background = DS.indigoLight; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#E2E8F0"; e.currentTarget.style.background = "#fff"; }}
                >
                  ← Back
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 18, marginBottom: 20 }}>

                {/* Project */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'Montserrat', sans-serif" }}>Project</label>
                  {isEngineer && currentProject ? (
                    <input type="text" readOnly value={`${currentProject.title} (${currentProject.project_uid})`}
                      style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 14, color: DS.dark, background: DS.grayLight, outline: "none", fontFamily: "'Montserrat', sans-serif", boxSizing: "border-box", height: "43px" }} />
                  ) : (
                    <select name="project_id" value={formData.project_id} onChange={handleChange}
                      style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 14, color: DS.dark, background: DS.surfaceAlt, outline: "none", fontFamily: "'Montserrat', sans-serif", boxSizing: "border-box", height: "43px" }}
                      onFocus={e => e.target.style.borderColor = DS.indigo} onBlur={e => e.target.style.borderColor = DS.border}>
                      <option value="">Select Project</option>
                      {projects.map(p => (<option key={p.id} value={p.id}>{p.title}</option>))}
                    </select>
                  )}
                </div>

                {/* CHANGE 12: Phase — searchable editable dropdown */}
                <SearchableDropdown
                  label="Phase"
                  value={formData.phase_name || (formData.phase_id ? getPhaseName(formData.phase_id) : "")}
                  options={phaseOptions}
                  placeholder="Search or type phase…"
                  onChange={handlePhaseChange}
                  focusColor={DS.indigo}
                />

               

{/* Start Date */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'Montserrat', sans-serif" }}>Start Date</label>
                  <input type="date" name="start_date" value={formData.start_date} onChange={handleChange}
                    style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 14, color: DS.dark, background: DS.surfaceAlt, outline: "none", fontFamily: "'Montserrat', sans-serif", boxSizing: "border-box", height: "43px", cursor: "pointer" }}
                    onFocus={e => e.target.style.borderColor = DS.indigo} onBlur={e => e.target.style.borderColor = DS.border}
                    onClick={e => { if (e.target.showPicker) { try { e.target.showPicker(); } catch (err) {} } }} />
                </div>

{/* End Date */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'Montserrat', sans-serif" }}>End Date</label>
                  <input type="date" name="end_date" value={formData.end_date} onChange={handleChange}
                    style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 14, color: DS.dark, background: DS.surfaceAlt, outline: "none", fontFamily: "'Montserrat', sans-serif", boxSizing: "border-box", height: "43px", cursor: "pointer" }}
                    onFocus={e => e.target.style.borderColor = DS.indigo} onBlur={e => e.target.style.borderColor = DS.border}
                    onClick={e => { if (e.target.showPicker) { try { e.target.showPicker(); } catch (err) {} } }} />
                </div>

                {/* Status toggle */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'Montserrat', sans-serif" }}>Status</label>
                  <div style={{ display: "flex", gap: 6, flexWrap: "nowrap" }}>
                    {[
                      { key: "pending",     label: "Pending",     col: DS.amber,   bg: DS.amberLight   },
                      { key: "in_progress", label: "In Progress", col: DS.indigo,  bg: DS.indigoLight  },
                      { key: "completed",   label: "Completed",   col: DS.emerald, bg: DS.emeraldLight },
                    ].map(opt => (
                      <button key={opt.key} type="button"
                        onClick={() => setFormData(prev => ({ ...prev, status: opt.key }))}
                        style={{
                          flex: 1, padding: "10px 0", borderRadius: 10,
                          border: `1.5px solid ${formData.status === opt.key ? opt.col : DS.border}`,
                          background: formData.status === opt.key ? opt.bg : DS.surfaceAlt,
                          color: formData.status === opt.key ? opt.col : DS.gray,
                          fontSize: 12, fontWeight: 700, cursor: "pointer",
                          fontFamily: "'Montserrat', sans-serif", outline: "none",
                          transition: "all 0.15s", minWidth: 90,
                        }}>
                        {opt.label}
                      </button>
                      ))}
                  </div>
                </div>
{/* Before Photo */}
<div>
  <label style={{ fontSize: 12, fontWeight: 600, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'Montserrat', sans-serif" }}>Before Photo</label>
  {existingPhotos.before_photo && !formData.before_photo && !removePhotoFlags.before_photo && (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, padding: "6px 10px", background: DS.surfaceAlt, border: `1.5px solid ${DS.border}`, borderRadius: 10 }}>
      <img
        src={`${UPLOADS_URL}${existingPhotos.before_photo}`}
        alt="Current before"
        style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 8, border: `1px solid ${DS.border}` }}
      />
      <div style={{ fontSize: 11, color: DS.gray, fontFamily: "'Montserrat', sans-serif", flex: 1 }}>
        Current photo on file.<br />Choose a new file below to replace it.
      </div>
      <button
  type="button"
  className="ws-photo-remove-btn"
  title="Remove photo"
  onClick={() => handleRemoveExistingPhoto("before_photo")}
>
  <X size={12} />
</button>
    </div>
  )}
  {removePhotoFlags.before_photo && !formData.before_photo && (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, padding: "6px 10px", background: DS.roseLight, border: `1.5px dashed #FECDD3`, borderRadius: 10, fontSize: 11, color: DS.rose, fontFamily: "'Montserrat', sans-serif" }}>
      Photo will be removed when you save.
    </div>
  )}
  <input type="file" name="before_photo" onChange={handleChange} accept="image/*"
    style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${DS.border}`, padding: "8px 14px", fontSize: 13, color: DS.dark, background: DS.surfaceAlt, outline: "none", fontFamily: "'Montserrat', sans-serif", boxSizing: "border-box", height: "43px" }} />
</div>
{/* After Photo */}
<div>
  <label style={{ fontSize: 12, fontWeight: 600, color: DS.gray, display: "block", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'Montserrat', sans-serif" }}>After Photo</label>
  {existingPhotos.after_photo && !formData.after_photo && !removePhotoFlags.after_photo && (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, padding: "6px 10px", background: DS.surfaceAlt, border: `1.5px solid ${DS.border}`, borderRadius: 10 }}>
      <img
        src={`${UPLOADS_URL}${existingPhotos.after_photo}`}
        alt="Current after"
        style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 8, border: `1px solid ${DS.border}` }}
      />
      <div style={{ fontSize: 11, color: DS.gray, fontFamily: "'Montserrat', sans-serif", flex: 1 }}>
        Current photo on file.<br />Choose a new file below to replace it.
      </div>
      <button
        type="button"
        className="ws-photo-remove-btn"
        title="Remove photo"
        onClick={() => handleRemoveExistingPhoto("after_photo")}
      >✕</button>
    </div>
  )}
  {removePhotoFlags.after_photo && !formData.after_photo && (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, padding: "6px 10px", background: DS.roseLight, border: `1.5px dashed #FECDD3`, borderRadius: 10, fontSize: 11, color: DS.rose, fontFamily: "'Montserrat', sans-serif" }}>
      Photo will be removed when you save.
    </div>
  )}
<input type="file" name="after_photo" onChange={handleChange} accept="image/*"
    style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${DS.border}`, padding: "8px 14px", fontSize: 13, color: DS.dark, background: DS.surfaceAlt, outline: "none", fontFamily: "'Montserrat', sans-serif", boxSizing: "border-box", height: "43px" }} />
</div>

</div>

<div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: DS.gray, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'Montserrat', sans-serif" }}>
                    Tasks for this Phase
                  </label>
                  <button
                    type="button"
                    onClick={addTaskRow}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      background: DS.indigoLight, color: DS.indigo,
                      border: "1.5px solid #C7D2FE", borderRadius: 8,
                      padding: "6px 12px", fontSize: 12, fontWeight: 700,
                      cursor: "pointer", fontFamily: "'Montserrat', sans-serif",
                    }}
                  >
                    <Plus size={13} /> Add Task
                  </button>
</div>

                {taskRows.map((row) => (
                  <div
                    key={row.id}
                    style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 10, marginBottom: 12, alignItems: "center" }}
                  >
                    <SearchableDropdown
                      value={row.task_name || (row.task_id ? getTaskName(row.task_id) : "")}
                      options={taskOptions}
                      placeholder="Search or type task…"
                      onChange={(val) => handleTaskRowChange(row.id, val)}
                      focusColor={DS.indigo}
                    />
                    <input
                      type="number" placeholder="Target sqmt" value={row.sqmt}
                      onChange={e => updateTaskRow(row.id, "sqmt", e.target.value)}
                      style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 14, color: DS.dark, background: DS.surfaceAlt, outline: "none", fontFamily: "'Montserrat', sans-serif", boxSizing: "border-box", height: "43px" }}
                    />
                    <input
                      type="number" placeholder="Completed sqmt" value={row.completed_sqmt}
                      onChange={e => updateTaskRow(row.id, "completed_sqmt", e.target.value)}
                      style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 14, color: DS.dark, background: DS.surfaceAlt, outline: "none", fontFamily: "'Montserrat', sans-serif", boxSizing: "border-box", height: "43px" }}
                    />
                    {taskRows.length > 1 && (
                      <button type="button" className="ws-photo-remove-btn" title="Remove task" onClick={() => removeTaskRow(row.id)}>
                        <X size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleSave}
                  style={{ background: "linear-gradient(135deg,#4F46E5,#6366F1)", color: "#fff", border: "none", padding: "11px 26px", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "'Montserrat', sans-serif", boxShadow: "0 4px 14px rgba(79,70,229,0.3)" }}>
                  {editId ? "Update Schedule" : "Save Schedule"}
                </button>
                <button onClick={handleReset}
                  style={{ background: DS.grayLight, color: DS.gray, border: `1.5px solid ${DS.border}`, padding: "11px 20px", borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'Montserrat', sans-serif" }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── TABLE CARD ──────────────────────────────────────────────────── */}
        <div style={{
          background: DS.surface, borderRadius: 20,
          border: `1px solid ${DS.border}`, overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}>
          <div style={{ height: 3, background: "linear-gradient(90deg,#4F46E5 0%,#7C3AED 33%,#E11D48 66%,#D97706 100%)" }} />

          {/* ── FILTERS ROW ─────────────────────────────────────────────── */}
          <div style={{
            padding: "16px 22px", borderBottom: `1px solid ${DS.border}`,
            background: "#FDFCFF",
          }}>
            {/* Toolbar top: title + search */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, fontSize: 15, color: DS.dark }}>
                  Work Schedule Directory
                </div>
                <div style={{ fontSize: 12, color: DS.faint, marginTop: 2, fontFamily: "'Montserrat', sans-serif" }}>
                  {filteredItems.length} of {workItems.length} items shown
                  {hasActiveFilters && (
                    <button onClick={clearFilters} style={{ marginLeft: 10, background: "none", border: "none", color: DS.indigo, fontSize: 11, fontWeight: 700, cursor: "pointer", padding: 0, fontFamily: "'Montserrat', sans-serif", textDecoration: "underline" }}>
                      Clear filters
                    </button>
                  )}
                </div>
              </div>

              {/* CHANGE 5: Search */}
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                background: DS.surfaceAlt, border: `1.5px solid ${DS.border}`,
                borderRadius: 12, padding: "9px 14px", minWidth: 280,
                transition: "border-color 0.15s",
              }}
                onFocus={e => e.currentTarget.style.borderColor = "#A5B4FC"}
                onBlur={e  => e.currentTarget.style.borderColor = DS.border}
              >
<Search size={15} color={DS.faint} style={{ flexShrink: 0 }} />                <input
                  type="text"
                  placeholder="Search project, phase, task, status…"
                  value={search}
                  onChange={e => handleFilterChange(setSearch)(e.target.value)}
                  style={{ border: "none", background: "transparent", fontSize: 13, color: DS.dark, outline: "none", width: "100%", fontFamily: "'Montserrat', sans-serif" }}
                />
                {search && (
  <button onClick={() => handleFilterChange(setSearch)("")}
    style={{ background: "none", border: "none", cursor: "pointer", color: DS.faint, padding: 0, lineHeight: 1, display: "flex" }}>
    <X size={14} />
  </button>
)}
              </div>
            </div>

            {/* CHANGES 8-11: Filter row */}
            <div className="ws-filters-grid" style={{
              display: "grid",
              gridTemplateColumns: !isEngineer
                ? "repeat(auto-fit, minmax(160px, 1fr))"
                : "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 10, alignItems: "end",
            }}>

              {/* CHANGE 8: Project filter (admin only) */}
              {!isEngineer && (
                <div>
                  <label style={{ fontSize: 10, fontWeight: 700, color: DS.faint, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 5, fontFamily: "'Montserrat', sans-serif" }}>Project</label>
                  <select
                    className="ws-filter-select"
                    value={filterProject}
                    onChange={e => handleFilterChange(setFilterProject)(e.target.value)}
                  >
                    <option value="">All Projects</option>
                    {projects.map(p => (
                      <option key={p.id} value={String(p.id)}>{p.title}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* CHANGE 9: Status filter */}
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: DS.faint, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 5, fontFamily: "'Montserrat', sans-serif" }}>Status</label>
                <select
                  className="ws-filter-select"
                  value={filterStatus}
                  onChange={e => handleFilterChange(setFilterStatus)(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  {uniqueStatuses.map(s => (
                    <option key={s} value={s}>{s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </div>

              {/* CHANGE 10: From date */}
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: DS.faint, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 5, fontFamily: "'Montserrat', sans-serif" }}>From Date</label>
<input
                  type="date"
                  className="ws-filter-input"
                  value={filterFrom}
                  onChange={e => handleFilterChange(setFilterFrom)(e.target.value)}
                  onClick={e => { if (e.target.showPicker) { try { e.target.showPicker(); } catch (err) {} } }}
                  style={{ cursor: "pointer" }}
                />
              </div>

              {/* CHANGE 10: To date */}
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: DS.faint, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 5, fontFamily: "'Montserrat', sans-serif" }}>To Date</label>
               <input
                  type="date"
                  className="ws-filter-input"
                  value={filterTo}
                  onChange={e => handleFilterChange(setFilterTo)(e.target.value)}
                  onClick={e => { if (e.target.showPicker) { try { e.target.showPicker(); } catch (err) {} } }}
                  style={{ cursor: "pointer" }}
                />
              </div>

              {/* CHANGE 11: Due task toggle */}
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: DS.faint, textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 5, fontFamily: "'Montserrat', sans-serif" }}>Due Tasks</label>
                <button
                  onClick={() => handleFilterChange(setFilterDue)(!filterDue)}
                  style={{
                    width: "100%", height: 38, borderRadius: 10,
                    border: `1.5px solid ${filterDue ? DS.rose : DS.border}`,
                    background: filterDue ? DS.roseLight : "#fff",
                    color: filterDue ? DS.rose : DS.gray,
                    fontSize: 12, fontWeight: 700,
                    cursor: "pointer", fontFamily: "'Montserrat', sans-serif",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                    transition: "all 0.15s",
                  }}
                >
{filterDue ? <><AlertTriangle size={12} /> Showing Due</> : "Show Overdue"}
                </button>
              </div>

            </div>
          </div>

          {/* ── TABLE ────────────────────────────────────────────────────── */}
          {/* CHANGE 7: Standardized table */}
          <div className="ws-table-scroll" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1020 }}>
              <thead>
                <tr style={{ background: "#FAFBFF", borderBottom: `2px solid ${DS.border}` }}>
{["#", "Phase & Task", "Sqmt Metrics", "Dates", "Status", "Progress", "Actions"].map((h, i) => (
                    <th key={h} style={{
                      fontSize: 10, fontWeight: 700, color: DS.faint,
                      textTransform: "uppercase", letterSpacing: "0.1em",
                      padding: "11px 18px",
                      textAlign: i === 0 ? "center" : "left",
                      fontFamily: "'Montserrat', sans-serif",
                      userSelect: "none",
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {/* Loading */}
                {loading && (
                  <tr>
                    <td colSpan={7} style={{ padding: "64px 20px", textAlign: "center" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
<div style={{ width: 52, height: 52, borderRadius: 14, background: DS.indigoLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
  <ClipboardList size={22} color={DS.indigo} />
</div>                        <div style={{ fontSize: 14, fontWeight: 600, color: DS.gray, fontFamily: "'Montserrat', sans-serif" }}>Loading work schedules…</div>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Empty */}
                {!loading && filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: "64px 20px", textAlign: "center" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
<div style={{ width: 56, height: 56, borderRadius: 16, background: DS.indigoLight, border: `1px solid #C7D2FE`, display: "flex", alignItems: "center", justifyContent: "center" }}>
  <ClipboardList size={24} color={DS.indigo} />
</div>                        <div style={{ fontSize: 15, fontWeight: 700, color: DS.dark, fontFamily: "'Montserrat', sans-serif" }}>
                          {hasActiveFilters ? "No matching schedules" : "No schedule items yet"}
                        </div>
                        <div style={{ fontSize: 13, color: DS.faint, fontFamily: "'Montserrat', sans-serif", maxWidth: 320 }}>
                          {hasActiveFilters
                            ? "Try adjusting your filters or clearing the search."
                            : "Click Add Schedule above to create one."}
                        </div>
                        {hasActiveFilters && (
                          <button onClick={clearFilters} style={{ marginTop: 6, background: DS.indigoLight, color: DS.indigo, border: `1px solid #C7D2FE`, borderRadius: 9, padding: "7px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Montserrat', sans-serif" }}>
                            Clear Filters
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                {/* CHANGE 4, 6: Rows with serial number + pagination slice */}
                {!loading && pageRows.map((item, i) => {
                  const st          = getStatusStyle(item.status);
                  const statusLabel = (item.status || "pending").replace(/_/g, " ");
                  const logsOpen    = expandedLogs.has(item.id);
                  const logsLoading = logLoading.has(item.id);
                  const logs        = logsMap[item.id] || [];
                  const isOverdue   = (() => {
                    if (!item.end_date) return false;
                    if ((item.status || "").toLowerCase() === "completed") return false;
                    return new Date(item.end_date) < today;
                  })();

                  return (
                    <>
                      <tr
                        key={`row-${item.id}`}
                        className="ws-tr"
                        style={{
                          borderBottom: logsOpen ? "none" : `1px solid #F1F5F9`,
                          background: DS.surface,
                        }}
                      >
                        {/* CHANGE 4: Serial number */}
                        <td style={{ padding: "15px 18px", textAlign: "center", width: 52 }}>
                          <span className="ws-row-num" style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 28, height: 28, borderRadius: 8,
                            background: DS.indigoLight, color: DS.indigo,
                            fontSize: 12, fontWeight: 700,
                            fontFamily: "'Montserrat', sans-serif",
                            transition: "background 0.12s, color 0.12s",
                          }}>
                            {pageStart + i + 1}
                          </span>
                        </td>

                      

                       {/* Phase & Task */}
<td style={{ padding: "15px 18px" }}>
  <div style={{ fontSize: 13, fontWeight: 700, color: DS.dark, fontFamily: "'Montserrat', sans-serif" }}>
    {item.phase_name || getPhaseName(item.phase_id) || item.phase_id || "—"}
  </div>
  <div style={{ fontSize: 11, color: DS.gray, marginTop: 3, fontFamily: "'Montserrat', sans-serif" }}>
    {item.task_name  || getTaskName(item.task_id)  || item.task_id  || "—"}
  </div>
  {isOverdue && (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, fontWeight: 700, color: DS.rose, background: DS.roseLight, padding: "2px 7px", borderRadius: 6, marginTop: 4, border: "1px solid #FECDD3" }}>
    <AlertTriangle size={10} /> Overdue
  </span>
)}
</td>
                        {/* Sqmt metrics */}
                        <td style={{ padding: "15px 18px" }}>
                          <div style={{ fontSize: 13, color: DS.dark, fontFamily: "'Montserrat', sans-serif" }}>
                            <strong style={{ fontWeight: 700 }}>{item.completed_sqmt ?? 0}</strong>
                            <span style={{ color: DS.faint }}> / {item.sqmt || "—"} sqmt</span>
                          </div>
                          {item.sqmt && item.completed_sqmt !== undefined && (
                            <div style={{ marginTop: 5, height: 4, borderRadius: 99, background: DS.grayLight, overflow: "hidden", width: 100 }}>
                              <div style={{
                                height: "100%", borderRadius: 99,
                                background: `linear-gradient(90deg, ${DS.emerald}, ${DS.teal})`,
                                width: `${Math.min(100, (Number(item.completed_sqmt) / Number(item.sqmt)) * 100)}%`,
                              }} />
                            </div>
                          )}
                        </td>

{/* Dates */}
<td style={{ padding: "15px 18px", whiteSpace: "nowrap" }}>
  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: DS.dark, fontWeight: 500, fontFamily: "'Montserrat', sans-serif" }}>
      <PlayCircle size={12} />
      {formatDate(item.start_date)}
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: DS.gray, fontFamily: "'Montserrat', sans-serif" }}>
      <Square size={10} />
      {formatDate(item.end_date)}
    </div>
  </div>
</td>

                        {/* Status */}
                        <td style={{ padding: "15px 18px" }}>
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            fontSize: 11, fontWeight: 700,
                            color: st.color, background: st.bg,
                            padding: "5px 12px", borderRadius: 20,
                            border: `1px solid ${st.border}`,
                            textTransform: "capitalize",
                            fontFamily: "'Montserrat', sans-serif",
                            whiteSpace: "nowrap",
                          }}>
                            <span style={{ width: 6, height: 6, borderRadius: "50%", background: st.color, flexShrink: 0 }} />
                            {statusLabel}
                          </span>
                        </td>

                      {/* Progress column — standalone, sits right after Status */}
                        <td style={{ padding: "15px 18px" }}>
                          <button
                            className="ws-btn-action"
                            onClick={() => openProgressModal(item.id)}
                            style={{ background: DS.emeraldLight, color: DS.emerald, borderColor: "#A7F3D0" }}
                            onMouseEnter={e => { e.currentTarget.style.background = DS.emerald; e.currentTarget.style.color = "#fff"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = DS.emeraldLight; e.currentTarget.style.color = DS.emerald; }}
                          ><Plus size={12} style={{ marginRight: 2 }} /> </button>
                        </td>

                        {/* CHANGE 7: Action buttons — View / Edit / Delete / Logs only */}
                        <td style={{ padding: "15px 18px" }}>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <button
                              className="ws-btn-action"
                              onClick={() => setViewItem(item)}
                              style={{ background: DS.tealLight, color: DS.teal, borderColor: "#99F6E4" }}
                              onMouseEnter={e => { e.currentTarget.style.background = DS.teal; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = DS.tealLight; e.currentTarget.style.color = DS.teal; }}
                            ><Eye size={12} style={{ marginRight: 2 }} /></button>
<button
  onClick={() => handleEdit(item)}
  style={{
    background: DS.indigoLight, color: DS.indigo,
    border: "1px solid #C7D2FE",
    padding: "7px 16px", borderRadius: 10,
    fontSize: 12, fontWeight: 700, cursor: "pointer",
    transition: "all 0.15s", whiteSpace: "nowrap",
    fontFamily: "'Montserrat', sans-serif",
  }}
  onMouseEnter={e => { e.currentTarget.style.background = DS.indigo; e.currentTarget.style.color = "#fff"; }}
  onMouseLeave={e => { e.currentTarget.style.background = DS.indigoLight; e.currentTarget.style.color = DS.indigo; }}
><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg></button>

                            <button
                              className="ws-btn-action"
                              onClick={() => handleDelete(item.id)}
                              style={{ background: DS.roseLight, color: DS.rose, borderColor: "#FECDD3" }}
                              onMouseEnter={e => { e.currentTarget.style.background = DS.rose; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = DS.roseLight; e.currentTarget.style.color = DS.rose; }}
                            ><Trash2 size={12} style={{ marginRight: 2 }} /> </button>

<button
  className="ws-btn-action"
  onClick={() => toggleLogs(item.id)}
  style={{
    background: logsOpen ? DS.violet : DS.violetLight,
    color: logsOpen ? "#fff" : DS.violet,
    borderColor: "#DDD6FE",
  }}
  onMouseEnter={e => { e.currentTarget.style.background = DS.violet; e.currentTarget.style.color = "#fff"; }}
  onMouseLeave={e => { e.currentTarget.style.background = logsOpen ? DS.violet : DS.violetLight; e.currentTarget.style.color = logsOpen ? "#fff" : DS.violet; }}
>{<ScrollText size={12} style={{ marginRight: 2 }} />}{logsOpen ? "Hide" : ""}</button>
                          </div>
                        </td>
                      </tr>

                      {/* Logs expansion row (UNCHANGED) */}
                      {logsOpen && (
                        <tr key={`logs-${item.id}`}>
                          <td colSpan={7} style={{ padding: 0, borderBottom: `1px solid ${DS.border}` }}>
                            <div style={{ background: "#FAFAFF", borderTop: `1px dashed #C7D2FE`, padding: "20px 28px 20px 52px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                                <div style={{ width: 4, height: 18, borderRadius: 4, background: DS.violet }} />
                                <span style={{ fontSize: 12, fontWeight: 700, color: DS.violet, textTransform: "uppercase", letterSpacing: "0.07em", fontFamily: "'Montserrat', sans-serif" }}>Progress Logs</span>
                                <span style={{ fontSize: 11, color: DS.faint, marginLeft: 4, fontFamily: "'Montserrat', sans-serif" }}>
                                  {logsLoading ? "Loading..." : `${logs.length} entr${logs.length === 1 ? "y" : "ies"}`}
                                </span>
                              </div>
                              {logsLoading && (<div style={{ fontSize: 13, color: DS.faint, padding: "12px 0", fontFamily: "'Montserrat', sans-serif" }}>Fetching logs…</div>)}
                              {!logsLoading && logs.length === 0 && (
                                <div style={{ border: `1.5px dashed ${DS.border}`, borderRadius: 12, padding: "20px 24px", textAlign: "center", color: DS.faint, fontSize: 13, fontFamily: "'Montserrat', sans-serif" }}>
                                  No progress logs yet. Click <strong style={{ color: DS.emerald }}>+ Progress</strong> to add one.
                                </div>
                              )}
                              {!logsLoading && logs.length > 0 && (
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
                                  {logs.map((log) => (
                                    <div key={log.id} style={{ background: DS.surface, border: `1.5px solid ${DS.border}`, borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                                      {log.progress_image && (
                                        <a href={`${UPLOADS_URL}${log.progress_image}`} target="_blank" rel="noreferrer" style={{ display: "block", textDecoration: "none" }}>
                                          <img src={`${UPLOADS_URL}${log.progress_image}`} alt="Progress" style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }} onError={e => { e.currentTarget.style.display = "none"; }} />
                                        </a>
                                      )}
<div style={{ padding: "12px 14px" }}>
                                        {log.comment && (<p style={{ margin: "0 0 8px", fontSize: 13, color: DS.dark, lineHeight: 1.6, fontFamily: "'Montserrat', sans-serif" }}>{log.comment}</p>)}
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 4 }}>
<span style={{ fontSize: 10, fontWeight: 700, color: DS.violet, background: DS.violetLight, padding: "3px 8px", borderRadius: 6, border: "1px solid #DDD6FE", fontFamily: "'Montserrat', sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}>
  <Calendar size={10} /> {formatDate(log.added_dt)}
</span>
                                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            {log.progress_image && (
<a href={`${UPLOADS_URL}${log.progress_image}`} target="_blank" rel="noreferrer" style={{ fontSize: 10, fontWeight: 700, color: DS.teal, textDecoration: "none", fontFamily: "'Montserrat', sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}>
  <ImageIcon size={10} /> View Full
</a>
                                            )}
                                            <button
                                              onClick={() => handleDeleteLog(log.id, item.id)}
                                              title="Delete log"
                                              style={{
                                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                width: 22, height: 22, borderRadius: 6,
                                                background: DS.roseLight, color: DS.rose,
                                                border: `1px solid #FECDD3`, cursor: "pointer",
                                                flexShrink: 0,
                                              }}
                                              onMouseEnter={e => { e.currentTarget.style.background = DS.rose; e.currentTarget.style.color = "#fff"; }}
                                              onMouseLeave={e => { e.currentTarget.style.background = DS.roseLight; e.currentTarget.style.color = DS.rose; }}
                                            >
                                              <Trash2 size={11} />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── FOOTER + PAGINATION (CHANGE 6) ──────────────────────────── */}
          <div style={{
            padding: "13px 22px", borderTop: `1px solid ${DS.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#FAFBFF", flexWrap: "wrap", gap: 10,
          }}>
            <span style={{ fontSize: 12, color: DS.faint, fontFamily: "'Montserrat', sans-serif" }}>
              Showing{" "}
              <strong style={{ color: DS.dark }}>
                {filteredItems.length === 0 ? 0 : pageStart + 1}–{Math.min(pageStart + ROWS_PER_PAGE, filteredItems.length)}
              </strong>
              {" "}of{" "}
              <strong style={{ color: DS.dark }}>{filteredItems.length}</strong> items
            </span>

            {totalPages > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <button className="ws-pg-btn" disabled={safePage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>‹</button>
                {getPageNumbers().map(p => (
                  <button key={p} className={`ws-pg-btn${p === safePage ? " active" : ""}`} onClick={() => setCurrentPage(p)}>{p}</button>
                ))}
                <button className="ws-pg-btn" disabled={safePage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>›</button>
              </div>
            )}
            {totalPages <= 1 && filteredItems.length > 0 && (
              <div>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg,#4F46E5,#6366F1)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, boxShadow: "0 2px 8px rgba(79,70,229,0.3)", fontFamily: "'Montserrat', sans-serif" }}>1</div>
              </div>
            )}
          </div>
        </div>

        {/* ── ADD PROGRESS MODAL (UNCHANGED) ──────────────────────────────── */}
        {progressModal.open && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.65)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 16 }}
            onClick={(e) => { if (e.target === e.currentTarget) closeProgressModal(); }}>
            <div style={{ background: DS.surface, borderRadius: 22, border: `1.5px solid ${DS.border}`, width: "100%", maxWidth: 460, boxShadow: "0 24px 48px rgba(0,0,0,0.18)", overflow: "hidden" }}>
              <div style={{ height: 4, background: "linear-gradient(90deg,#059669,#4F46E5,#D97706)" }} />
              <div style={{ padding: "26px 28px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
<div style={{ width: 38, height: 38, borderRadius: 10, background: DS.emeraldLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
  <Camera size={18} color={DS.emerald} />
</div>                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16, color: DS.dark, fontFamily: "'Montserrat', sans-serif" }}>Add Progress Log</div>
                    <div style={{ fontSize: 11, color: DS.gray, marginTop: 2, fontFamily: "'Montserrat', sans-serif" }}>Task #{progressModal.wid}</div>
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 7, fontFamily: "'Montserrat', sans-serif" }}>Comment</label>
                  <textarea rows={4} placeholder="Describe the progress made on this task…" value={progressForm.comment} onChange={e => setProgressForm(prev => ({ ...prev, comment: e.target.value }))}
                    style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${DS.border}`, padding: "11px 14px", fontSize: 14, color: DS.dark, background: DS.surfaceAlt, outline: "none", fontFamily: "'Montserrat', sans-serif", boxSizing: "border-box", resize: "vertical" }}
                    onFocus={e => e.target.style.borderColor = DS.emerald} onBlur={e => e.target.style.borderColor = DS.border} />
                </div>
                <div style={{ marginBottom: 22 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: DS.gray, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 7, fontFamily: "'Montserrat', sans-serif" }}>Progress Image (optional)</label>
                  <input ref={progressImageRef} type="file" accept="image/*" onChange={e => setProgressForm(prev => ({ ...prev, image: e.target.files[0] || null }))}
                    style={{ width: "100%", borderRadius: 12, border: `1.5px solid ${DS.border}`, padding: "8px 14px", fontSize: 13, color: DS.dark, background: DS.surfaceAlt, outline: "none", fontFamily: "'Montserrat', sans-serif", boxSizing: "border-box", height: "43px" }} />
{progressForm.image && (
  <p style={{ margin: "6px 0 0", fontSize: 11, color: DS.emerald, fontFamily: "'Montserrat', sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
    <CheckCircle2 size={12} /> {progressForm.image.name}
  </p>
)}                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={handleAddLog} disabled={progressSaving}
                    style={{ flex: 1, background: progressSaving ? DS.grayLight : "linear-gradient(135deg,#059669,#0D9488)", color: progressSaving ? DS.faint : "#fff", border: "none", padding: "12px 0", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: progressSaving ? "not-allowed" : "pointer", fontFamily: "'Montserrat', sans-serif", boxShadow: progressSaving ? "none" : "0 4px 14px rgba(5,150,105,0.3)" }}>
                    {progressSaving ? "Saving…" : "Save Log"}
                  </button>
                  <button onClick={closeProgressModal} disabled={progressSaving}
                    style={{ background: DS.grayLight, color: DS.gray, border: `1.5px solid ${DS.border}`, padding: "12px 20px", borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'Montserrat', sans-serif" }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── VIEW DETAIL MODAL (UNCHANGED structure, Roboto applied) ──────── */}
        {viewItem && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }}>
            <div style={{ background: DS.surface, borderRadius: 24, border: `1.5px solid ${DS.border}`, padding: 32, width: "100%", maxWidth: "500px", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
<Calendar size={22} color={DS.indigo} />
                <h3 style={{ margin: 0, fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 20, color: DS.dark }}>Schedule Detail</h3>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: 14 }}>
                {[
                  { label: "Project Title",           value: viewItem.project_title || getProjectName(viewItem.project_id), bold: true },
                  { label: "Phase",                   value: viewItem.phase_name    || getPhaseName(viewItem.phase_id)    || viewItem.phase_id || "—" },
                  { label: "Task",                    value: viewItem.task_name     || getTaskName(viewItem.task_id)     || viewItem.task_id  || "—" },
                  { label: "Target Sqmt",             value: viewItem.sqmt           ? `${viewItem.sqmt} sqmt`           : "—" },
                  { label: "Completed Sqmt",          value: viewItem.completed_sqmt ? `${viewItem.completed_sqmt} sqmt` : "—" },
                  { label: "Start Date",              value: viewItem.start_date     ? viewItem.start_date.split("T")[0]  : "—" },
                  { label: "End Date",                value: viewItem.end_date       ? viewItem.end_date.split("T")[0]    : "—" },
                  { label: "Status", value: (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      fontSize: 11, fontWeight: 700,
                      color: getStatusStyle(viewItem.status).color,
                      background: getStatusStyle(viewItem.status).bg,
                      padding: "3px 10px", borderRadius: 20, textTransform: "capitalize",
                      fontFamily: "'Montserrat', sans-serif",
                    }}>
                      {(viewItem.status || "pending").replace(/_/g, " ")}
                    </span>
                  )},
                ].map((row, index) => (
                  <div key={index} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${DS.grayLight}`, paddingBottom: 8 }}>
                    <span style={{ color: DS.gray, fontWeight: 600, fontFamily: "'Montserrat', sans-serif" }}>{row.label}</span>
                    <span style={{ color: DS.dark, fontWeight: row.bold ? 800 : 600, fontFamily: "'Montserrat', sans-serif" }}>{row.value}</span>
                  </div>
                ))}
                {viewItem.before_photo && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${DS.grayLight}`, paddingBottom: 8 }}>
                    <span style={{ color: DS.gray, fontWeight: 600, fontFamily: "'Montserrat', sans-serif" }}>Before Photo</span>
                    <a href={`${UPLOADS_URL}${viewItem.before_photo}`} target="_blank" rel="noreferrer" style={{ color: DS.violet, fontWeight: 700, textDecoration: "none", fontFamily: "'Montserrat', sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}>
  <ImageIcon size={14} /> View
</a>
                  </div>
                )}
                {viewItem.after_photo && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${DS.grayLight}`, paddingBottom: 8 }}>
                    <span style={{ color: DS.gray, fontWeight: 600, fontFamily: "'Montserrat', sans-serif" }}>After Photo</span>
                    <a href={`${UPLOADS_URL}${viewItem.after_photo}`} target="_blank" rel="noreferrer" style={{ color: DS.violet, fontWeight: 700, textDecoration: "none", fontFamily: "'Montserrat', sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}>
  <ImageIcon size={14} /> View
</a>
                  </div>
                )}
              </div>
              <button onClick={() => setViewItem(null)}
                style={{ marginTop: 24, width: "100%", border: "none", padding: "12px 0", borderRadius: 12, background: "linear-gradient(135deg,#4F46E5,#6366F1)", color: "#fff", fontWeight: 700, cursor: "pointer", fontFamily: "'Montserrat', sans-serif", boxShadow: "0 4px 10px rgba(79,70,229,0.25)" }}>
                Close
              </button>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}